import { useState } from 'react'
import { Button } from 'antd'
import { ExclamationCircleFilled, PlusCircleFilled } from '@ant-design/icons'
import { Horizontal, NothingMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import { OrderAlert } from './components/OrderAlert'
import { ControlPanelAlert } from './components/ControlPanelAlert'
import { useSiteAlerts } from '../useSiteAlerts'
import { alertTypes, type AlertType } from './types'
import {
  CarrierOrderRejectAlert,
  PotentialMisdropAlert,
  SupplyNeedsAttentionAlert,
  TrailerSwapAlert,
  WrongLocationAlert,
} from './components/Alerts'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'
import { panelModes } from '../UserControlPanel'

export type AlertsDisplayProps = {
  setMode: (mode: string) => void
  setSelectedAlert: (alert: AlertType) => void
}

export function AlertsDisplay({ setMode, setSelectedAlert }: AlertsDisplayProps) {
  const { siteAlertsResponse, handleResolveAlert } = useSiteAlerts()
  const { unresolved_count: unresolvedCount = 0, unresolved = [], resolved = [] } = siteAlertsResponse || {}
  const [showingResolved, setShowingResolved] = useState(unresolvedCount === 0)
  const noAlerts = !siteAlertsResponse || (unresolvedCount === 0 && resolved.length === 0)
  const { scopes } = useSecurityContext()

  return (
    <Vertical height={'auto'} className='mt-4' style={{ overflow: 'hidden' }}>
      <Horizontal flex={'none'} justifyContent='space-between' verticalCenter className='px-4 mb-2'>
        <Texto appearance='medium' className='flex-1' category='heading-small'>
          Recent Alerts
        </Texto>
        <ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} className='mr-2' />
        <Texto align='right' appearance={unresolvedCount === 0 ? 'light' : 'error'}>
          {unresolvedCount} Unresolved Alerts
        </Texto>
      </Horizontal>
      <Vertical height={'auto'} style={{ overflow: 'auto' }} gap={8}>
        {noAlerts && <NothingMessage title='No Alerts' message='You have not received any alerts.' />}
        {unresolved.map((a, i) => (
          <SingleAlertByType
            key={i}
            alert={a}
            handleResolve={() => handleResolveAlert(a.id)}
            scopes={scopes}
            setMode={setMode}
            setSelectedAlert={setSelectedAlert}
          />
        ))}
        {showingResolved && resolved.length > 0 ? (
          <Vertical flex='none' style={{ overflow: 'visible' }} className='mt-4' height={'auto'} gap={8}>
            <Texto appearance='medium' className='flex-1 px-4' category='heading-small'>
              Resolved Alerts
            </Texto>
            {resolved.map((a, i) => (
              <SingleAlertByType
                key={i}
                alert={a}
                handleResolve={() => handleResolveAlert(a.id)}
                scopes={scopes}
                setMode={setMode}
                setSelectedAlert={setSelectedAlert}
              />
            ))}
          </Vertical>
        ) : (
          <Horizontal horizontalCenter flex='none' style={{ overflow: 'visible' }} className='mt-4' height={'auto'}>
            <Button
              onClick={() => setShowingResolved(true)}
              icon={<PlusCircleFilled />}
              disabled={unresolvedCount === 0}
              type='link'
            >
              View Resolved Alerts
            </Button>
          </Horizontal>
        )}
      </Vertical>
    </Vertical>
  )
}

type SingleAlertByTypeProps = {
  alert: AlertType
  handleResolve: () => void
  scopes: Partial<Gravitate.AuthorizationResponse>
  setMode: (mode: string) => void
  setSelectedAlert: (alert: AlertType) => void
}

export function SingleAlertByType({ alert, handleResolve, scopes, setMode, setSelectedAlert }: SingleAlertByTypeProps) {
  switch (alert.type) {
    case alertTypes.TRAILER_SWAP:
      return (
        <TrailerSwapAlert
          alert={alert}
          handleViewTrailerSwap={() => {
            setMode(panelModes.TRAILERSWAPDETAILS)
            setSelectedAlert(alert)
          }}
          handleResolve={handleResolve}
          scopes={scopes}
        />
      )
    case alertTypes.POTENTIAL_MISDROP:
      return <PotentialMisdropAlert alert={alert} handleResolve={handleResolve} scopes={scopes} />
    case alertTypes.WRONG_LOCATION:
      return <WrongLocationAlert alert={alert} handleResolve={handleResolve} />
    case alertTypes.SUPPLY_NEEDS_ATTENTION:
      return <SupplyNeedsAttentionAlert alert={alert} handleResolve={handleResolve} scopes={scopes} />
    case alertTypes.CARRIER_ORDER_REJECT:
      return <CarrierOrderRejectAlert alert={alert} handleResolve={handleResolve} scopes={scopes} />
    case alertTypes.VOLUME_DISCREPANCY:
    case alertTypes.PRODUCT_MISMATCH:
    case alertTypes.SUPPLIER_MISMATCH:
      return <OrderAlert alert={alert} handleResolve={handleResolve} />
    default:
      ;<ControlPanelAlert alert={alert} handleResolve={handleResolve} />
  }
}
